import { useCallback } from 'react'

import { useAppDispatch, useAppSelector } from 'store/store'

import * as actions from './actions'
import { CommentParams, NewsParams } from './types'

export const useNews = () => {
  const dispatch = useAppDispatch()
  const authState = useAppSelector(({ auth }) => auth)

  const getAllNews = useCallback(() => {
    dispatch(actions.getAllNews())
  }, [dispatch])

  const getPaginatedNews = useCallback(
    (
      page: number = 1,
      limit: number = 20,
      filter?: 'new' | 'hot' | 'top' | 'boosted',
    ) => {
      const userId = authState.user?._id
      return dispatch(actions.getPaginatedNews({ page, limit, userId, filter }))
    },
    [dispatch, authState.user?._id],
  )

  const getNewsDetails = useCallback(
    (newsId: string) => {
      dispatch(actions.getNewsDetails({ newsId }))
    },
    [dispatch],
  )

  // const getNewsByGame = useCallback(
  //   (gameId: string) => {
  //     dispatch(actions.getAllNewsWithKeyword(gameId))
  //   },
  //   [dispatch],
  // )

  const createNews = useCallback(
    (payload: NewsParams) => {
      dispatch(actions.createNews(payload))
    },
    [dispatch],
  )
  const updateNews = useCallback(
    (payload: NewsParams) => {
      dispatch(actions.updateNews(payload))
    },
    [dispatch],
  )
  const deleteNews = useCallback(
    (newsId: string) => {
      dispatch(actions.deleteNews(newsId))
    },
    [dispatch],
  )

  const commentNews = useCallback(
    (payload: any) => {
      dispatch(actions.commentNews(payload))
    },
    [dispatch],
  )

  const likeNews = useCallback(
    (newsId: string) => {
      const userId = authState.user?._id

      if (userId) dispatch(actions.likeNews({ userId, newsId }))
    },
    [authState, dispatch],
  )

  const reshoutNews = useCallback(
    (newsId: string) => {
      const userId = authState.user?._id

      if (userId) dispatch(actions.reshoutNews({ userId, newsId }))
    },
    [authState, dispatch],
  )

  const getMediumPosts = useCallback(() => {
    dispatch(actions.getMediumPosts())
  }, [dispatch])

  const createNewsForGame = useCallback(
    (payload: NewsParams) => {
      dispatch(actions.createNewsForGame(payload))
    },
    [dispatch],
  )

  const getNewsByGameId = useCallback(
    (gameId: string) => {
      dispatch(actions.getNewsByGameId(gameId))
    },
    [dispatch],
  )

  const createQuoteShout = useCallback(
    (body: string, quotedPostId: string) => {
      return dispatch(actions.createQuoteShout({ body, quotedPostId }))
    },
    [dispatch],
  )

  const deleteComment = useCallback(
    (newsId: string, commentId: string) => {
      dispatch(actions.deleteComment({ newsId, commentId }))
    },
    [dispatch],
  )

  const likeComment = useCallback(
    async (commentId: string) => {
      const result = await dispatch(actions.likeComment(commentId))
      if (actions.likeComment.fulfilled.match(result)) {
        return result.payload
      }
      return null
    },
    [dispatch],
  )

  const loadCommentReplies = useCallback(
    async (commentId: string) => {
      const result = await dispatch(actions.getCommentReplies(commentId))
      if (actions.getCommentReplies.fulfilled.match(result)) {
        return result.payload
      }
      return null
    },
    [dispatch],
  )

  const newsState = useAppSelector(({ news }) => news)

  return {
    ...newsState,
    getAllNews,
    getPaginatedNews,
    getNewsDetails,
    getMediumPosts,
    // getNewsByGame,
    createNews,
    updateNews,
    deleteNews,
    commentNews,
    likeNews,
    reshoutNews,
    createNewsForGame,
    getNewsByGameId,
    createQuoteShout,
    deleteComment,
    likeComment,
    loadCommentReplies,
  }
}
